import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Plus, Search, Edit2, Trash2, X, Save, User, Mail, Phone,
  Shield, CheckCircle, AlertCircle, UserCog, Users as UsersIcon, LogIn, Eye, EyeOff,
  Calendar, Link2, Unlink
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../services/api';
import BatchUploadModal from '../../components/BatchUploadModal';

const AdminUsers = () => {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [displayPassword, setDisplayPassword] = useState('');
  const [connectingCalendar, setConnectingCalendar] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const passwordTimeoutRef = useRef(null);
  
  const primaryColor = settings?.primaryColor || '#00b30c';
  
  // Get current user and check if admin
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'Administrador';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'comercial',
    status: 'active',
    avatar: '',
    commissionPercentage: 7,
    permissions: {
      viewLeads: true,
      editLeads: true,
      deleteLeads: false,
      viewDeals: true,
      editDeals: true,
      viewStatistics: true,
      viewAllUsers: false,
      manageUsers: false,
      manageSettings: false,
    },
  });

  const roles = [
    { id: 'admin', name: 'Administrador', color: 'bg-purple-100 text-purple-700' },
    { id: 'comercial', name: 'Comercial', color: 'bg-green-100 text-green-700' },
    { id: 'referidor', name: 'Referidor', color: 'bg-amber-100 text-amber-700' },
    { id: 'asistente', name: 'Asistente', color: 'bg-teal-100 text-teal-700' },
    { id: 'trafficker', name: 'Trafficker', color: 'bg-orange-100 text-orange-700' },
    { id: 'viewer', name: 'Solo Lectura', color: 'bg-slate-100 text-slate-700' },
  ];

  // Load users - commercial users only see themselves
  useEffect(() => {
    loadUsers();
  }, [isAdmin]);

  // Handle Google Calendar callback
  useEffect(() => {
    const calendarConnected = searchParams.get('calendar_connected');
    const calendarError = searchParams.get('calendar_error');
    
    if (calendarConnected === 'true') {
      toast.success('¡Google Calendar conectado exitosamente!');
      loadUsers(); // Reload to show updated status
      // Clean URL
      navigate('/admin/users', { replace: true });
    } else if (calendarError) {
      toast.error(`Error al conectar Google Calendar: ${calendarError}`);
      navigate('/admin/users', { replace: true });
    }
  }, [searchParams, navigate]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await api.getUsers();
      // Commercial users only see themselves
      if (!isAdmin) {
        const filteredData = (data || []).filter(u => u.id === currentUser.id);
        setUsers(filteredData);
      } else {
        setUsers(data || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Google Calendar handlers
  const handleConnectCalendar = async (userId) => {
    setConnectingCalendar(userId);
    try {
      const response = await api.getCalendarAuthUrl(userId);
      if (response.authorization_url) {
        window.location.href = response.authorization_url;
      }
    } catch (error) {
      console.error('Error getting calendar auth URL:', error);
      toast.error('Error al iniciar conexión con Google Calendar');
      setConnectingCalendar(null);
    }
  };

  const handleDisconnectCalendar = async (userId) => {
    if (!confirm('¿Estás seguro de desconectar Google Calendar?')) return;
    
    try {
      await api.disconnectCalendar(userId);
      toast.success('Google Calendar desconectado');
      loadUsers();
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      toast.error('Error al desconectar Google Calendar');
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Stats
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.status === 'active').length;
  const comerciales = users.filter(u => u.role === 'comercial').length;
  const referidores = users.filter(u => u.role === 'referidor').length;

  // Handle password input with last character visible effect
  const handlePasswordChange = (e) => {
    const newValue = e.target.value;
    setFormData({ ...formData, password: newValue });
    
    // Clear previous timeout
    if (passwordTimeoutRef.current) {
      clearTimeout(passwordTimeoutRef.current);
    }
    
    // Show last character briefly
    if (newValue.length > 0) {
      const masked = '•'.repeat(Math.max(0, newValue.length - 1)) + newValue.slice(-1);
      setDisplayPassword(masked);
      
      // After 800ms, hide the last character too
      passwordTimeoutRef.current = setTimeout(() => {
        setDisplayPassword('•'.repeat(newValue.length));
      }, 800);
    } else {
      setDisplayPassword('');
    }
  };

  // Handle form
  const handleOpenModal = (user = null) => {
    setShowPassword(false);
    setDisplayPassword('');
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '', // Don't load password
        role: user.role || 'comercial',
        status: user.status || 'active',
        avatar: user.avatar || '',
        commissionPercentage: user.commissionPercentage || 7,
        permissions: user.permissions || {
          viewLeads: true,
          editLeads: true,
          deleteLeads: false,
          viewDeals: true,
          editDeals: true,
          viewStatistics: true,
          viewAllUsers: false,
          manageUsers: false,
          manageSettings: false,
        },
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'comercial',
        status: 'active',
        avatar: '',
        commissionPercentage: 7,
        permissions: {
          viewLeads: true,
          editLeads: true,
          deleteLeads: false,
          viewDeals: true,
          editDeals: true,
          viewStatistics: true,
          viewAllUsers: false,
          manageUsers: false,
          manageSettings: false,
        },
      });
    }
    setShowModal(true);
  };

  const handleRoleChange = (role) => {
    let permissions = { ...formData.permissions };
    
    if (role === 'admin') {
      permissions = {
        viewLeads: true, editLeads: true, deleteLeads: true,
        viewDeals: true, editDeals: true,
        viewStatistics: true, viewAllUsers: true,
        manageUsers: true, manageSettings: true,
      };
    } else if (role === 'comercial') {
      permissions = {
        viewLeads: true, editLeads: true, deleteLeads: false,
        viewDeals: true, editDeals: true,
        viewStatistics: true, viewAllUsers: false,
        manageUsers: false, manageSettings: false,
      };
    } else if (role === 'viewer') {
      // Viewer can see everything except admin sections
      permissions = {
        viewLeads: true, editLeads: false, deleteLeads: false,
        viewDeals: true, editDeals: false,
        viewStatistics: true, viewAllUsers: false,
        manageUsers: false, manageSettings: false,
      };
    } else if (role === 'trafficker') {
      permissions = {
        viewLeads: true, editLeads: false, deleteLeads: false,
        viewDeals: false, editDeals: false,
        viewStatistics: false, viewAllUsers: false,
        manageUsers: false, manageSettings: false,
      };
    } else if (role === 'asistente') {
      permissions = {
        viewLeads: true, editLeads: true, deleteLeads: false,
        viewDeals: true, editDeals: true,
        viewStatistics: true, viewAllUsers: false,
        manageUsers: false, manageSettings: false,
      };
    } else { // referidor or other
      permissions = {
        viewLeads: true, editLeads: false, deleteLeads: false,
        viewDeals: false, editDeals: false,
        viewStatistics: false, viewAllUsers: false,
        manageUsers: false, manageSettings: false,
      };
    }
    
    setFormData({ ...formData, role, permissions });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('El email es requerido');
      return;
    }

    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, formData);
        toast.success('Usuario actualizado');
      } else {
        await api.createUser(formData);
        toast.success('Usuario creado');
      }
      setShowModal(false);
      loadUsers();
    } catch (error) {
      toast.error('Error al guardar usuario');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.deleteUser(userId);
      toast.success('Usuario eliminado');
      setShowDeleteConfirm(null);
      loadUsers();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await api.updateUser(user.id, { ...user, status: newStatus });
      toast.success(`Usuario ${newStatus === 'active' ? 'activado' : 'desactivado'}`);
      loadUsers();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleImpersonate = async (user) => {
    setImpersonating(user.id);
    try {
      const response = await api.impersonateUser(user.id);
      
      // Save original admin session for "return to admin" feature
      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      localStorage.setItem('adminToken', currentToken);
      localStorage.setItem('adminUser', currentUser);
      
      // Store the new token and user data
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('isImpersonating', 'true');
      
      toast.success(`Sesión iniciada como ${user.name}`);
      
      // Redirect based on user role
      if (response.user.role === 'referidor') {
        window.location.href = '/portal-referidos';
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error impersonating user:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión como usuario');
      setImpersonating(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {isAdmin ? 'Usuarios' : 'Mi Perfil'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? 'Gestiona los usuarios y permisos del sistema' : 'Administra tu información personal'}
          </p>
        </div>
        
        {/* Only admins can create new users */}
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBatchModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 bg-white text-slate-700 rounded-lg hover:bg-slate-50 transition-all"
              data-testid="batch-users-btn"
            >
              <UsersIcon className="w-5 h-5" />
              Crear en Lote
            </button>
            <button
              onClick={() => navigate('/admin/users/new')}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all"
              style={{ backgroundColor: primaryColor }}
            >
              <Plus className="w-5 h-5" />
              Nuevo Usuario
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards - Only visible for admins */}
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50">
                <UsersIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Usuarios</p>
                <p className="text-2xl font-bold text-slate-900">{totalUsers}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Usuarios Activos</p>
                <p className="text-2xl font-bold text-green-600">{activeUsers}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                <User className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Comerciales</p>
                <p className="text-2xl font-bold" style={{ color: primaryColor }}>{comerciales}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Only visible for admins */}
      {isAdmin && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar usuarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
          >
            <option value="">Todos los roles</option>
            {roles.map(role => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <UsersIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No hay usuarios</h3>
          <p className="text-slate-500 mb-4">Crea tu primer usuario para comenzar</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Usuario</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Rol</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Google Calendar</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map(user => {
                const role = roles.find(r => r.id === user.role) || roles[2];
                const calendarConnected = user.googleCalendar?.connected;
                return (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold">
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            user.name?.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${role.color}`}>
                        {role.name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(user)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors ${
                          user.status === 'active' 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {user.status === 'active' ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      {calendarConnected ? (
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                            <Calendar className="w-3 h-3" />
                            Conectado
                          </span>
                          <button
                            onClick={() => handleDisconnectCalendar(user.id)}
                            className="p-1.5 hover:bg-red-50 rounded transition-colors"
                            title="Desconectar Google Calendar"
                          >
                            <Unlink className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleConnectCalendar(user.id)}
                          disabled={connectingCalendar === user.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                        >
                          {connectingCalendar === user.id ? (
                            <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Link2 className="w-3.5 h-3.5" />
                          )}
                          Conectar
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {/* Impersonate - Only for admins and not for yourself */}
                        {isAdmin && user.id !== currentUser.id && (
                          <button
                            onClick={() => handleImpersonate(user)}
                            disabled={impersonating === user.id}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                            title="Ingresar como este usuario"
                            data-testid={`impersonate-${user.id}`}
                          >
                            {impersonating === user.id ? (
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <LogIn className="w-4 h-4 text-blue-500 group-hover:text-blue-700" />
                            )}
                          </button>
                        )}
                        {/* Edit - Admins can edit anyone, commercial can only edit themselves */}
                        {(isAdmin || user.id === currentUser.id) && (
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            data-testid={`edit-${user.id}`}
                          >
                            <Edit2 className="w-4 h-4 text-slate-500" />
                          </button>
                        )}
                        {/* Delete - Only for admins and not for yourself */}
                        {isAdmin && user.id !== currentUser.id && (
                          <button
                            onClick={() => setShowDeleteConfirm(user.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-xl font-semibold text-slate-900">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Juan Pérez"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="juan@empresa.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {editingUser ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'text'}
                      value={showPassword ? formData.password : displayPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 pr-12 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                      placeholder={editingUser ? 'Ingresa nueva contraseña' : 'Mínimo 6 caracteres'}
                      style={{ fontFamily: showPassword ? 'inherit' : 'monospace' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {editingUser && (
                    <p className="text-xs text-slate-500 mt-1">Solo completa este campo si deseas cambiar la contraseña del usuario</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Rol</label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    disabled={!isAdmin}
                    className={`w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {roles.map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                  {!isAdmin && (
                    <p className="text-xs text-slate-500 mt-1">Solo un administrador puede cambiar el rol</p>
                  )}
                </div>
              </div>

              {/* Permissions - Only visible for admins */}
              {isAdmin && (
                <div className="border-t border-slate-100 pt-4">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Permisos
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'viewLeads', label: 'Ver Leads' },
                      { key: 'editLeads', label: 'Editar Leads' },
                      { key: 'deleteLeads', label: 'Eliminar Leads' },
                      { key: 'viewDeals', label: 'Ver Negocios' },
                      { key: 'editDeals', label: 'Editar Negocios' },
                      { key: 'viewStatistics', label: 'Ver Estadísticas' },
                      { key: 'viewAllUsers', label: 'Ver Todos los Usuarios' },
                      { key: 'manageUsers', label: 'Gestionar Usuarios' },
                      { key: 'manageSettings', label: 'Gestionar Configuración' },
                    ].map(perm => (
                      <label key={perm.key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.permissions[perm.key]}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, [perm.key]: e.target.checked }
                          })}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-slate-700">{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                <Save className="w-4 h-4" />
                {editingUser ? 'Actualizar' : 'Crear Usuario'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">¿Eliminar usuario?</h3>
                <p className="text-sm text-slate-500">Esta acción no se puede deshacer</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Upload Modal */}
      <BatchUploadModal
        open={showBatchModal}
        onOpenChange={setShowBatchModal}
        title="Crear Usuarios en Lote"
        description="Sube un archivo CSV o Excel con las columnas: nombre, email, contraseña, rol. Los roles válidos son: admin, comercial, referidor, trafficker, viewer."
        templateColumns={['nombre', 'email', 'contraseña', 'rol']}
        templateFileName="plantilla_usuarios.csv"
        onUpload={async (file) => {
          const result = await api.batchCreateUsers(file);
          if (result.created > 0) loadUsers();
          return result;
        }}
      />
    </div>
  );
};

export default AdminUsers;
